import { CreateMultilegDialog, useCreateMultilegDialog } from '@src/components/MultilegCombo/CreateMultilegDialog';
import {
  SymbolSelector as BaseSymbolSelector,
  Button,
  ButtonVariants,
  FormControlSizes,
  HStack,
  IndicatorBadge,
  MixpanelEvent,
  ProductTypeEnum,
  SymbolSelectorAppearance,
  Text,
  useCallbackRef,
  useMixpanel,
  useSecuritiesContext,
  type AutocompleteGroup,
  type AutocompleteSearchResult,
  type Security,
} from '@talos/kyoko';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useFeatureFlag } from '../../hooks';
import { usePanelContext } from '../Layout/providers/PanelContextProvider';
import type { AvaSymbolSelectorProps } from './types';

/**
 * Wraps the SymbolSelector and applies the options we want within the Principal UI
 */
export const SymbolSelector = memo(function SymbolSelector({
  dropdownPlacement = 'bottom',
  dropdownWidth,
  maxHeight = 240,
  appearance = SymbolSelectorAppearance.Basic,
  showAddMultiLegButton = true,
  excludeProductTypes,
  ...props
}: AvaSymbolSelectorProps) {
  const onSymbolChanged = useCallbackRef(props.onSymbolChanged);
  const closeMenuRef = useRef<(() => void) | null>(null);
  const mixpanel = useMixpanel();
  const createMultilegDialog = useCreateMultilegDialog({
    onUseSymbol: onSymbolChanged,
    useSymbolLabel: 'Use symbol',
  });
  const { enableCalendarSpreads, enablePredictionMarkets, enableSEDOLDisplay } = useFeatureFlag();
  const { searchableSecurities } = useSecuritiesContext();
  const { isPopout } = usePanelContext();

  const availableSecurities = useMemo(() => {
    const source = props.availableSecurities ?? searchableSecurities;
    const excludedProductTypes = new Set(excludeProductTypes);
    if (!enableCalendarSpreads) {
      excludedProductTypes.add(ProductTypeEnum.CalendarSpread);
    }
    if (!enablePredictionMarkets) {
      excludedProductTypes.add(ProductTypeEnum.PredictionOutcome);
    }
    return source.filter(security => !excludedProductTypes.has(security.ProductType));
  }, [
    enableCalendarSpreads,
    enablePredictionMarkets,
    excludeProductTypes,
    props.availableSecurities,
    searchableSecurities,
  ]);

  const { open: openCreateMultilegDialog } = createMultilegDialog;
  const handleAdd = useCallback(() => {
    openCreateMultilegDialog();
    closeMenuRef.current?.();
    mixpanel.track(MixpanelEvent.CreateNewMultileg);
  }, [openCreateMultilegDialog, mixpanel]);

  const OrderFormGroupHeader = useMemo(() => {
    return ({ group }: { group: AutocompleteGroup<AutocompleteSearchResult<Security>> }) => {
      return (
        <HStack
          gap="spacing4"
          justifyContent="flex-start"
          background="backgroundDropdownGroupHeader"
          py="spacing4"
          px="spacing12"
          fontSize="fontSizeTiny"
          data-testid="dropdown-group-header"
        >
          <Text size="fontSizeTiny" textTransform="uppercase" style={{ fontWeight: 500 }}>
            {group.group}
          </Text>
          <IndicatorBadge children={group.items?.length} />
        </HStack>
      );
    };
  }, []);

  const suffixCb = useCallback(
    ({ selectedGroup }: { selectedGroup?: string }) => {
      if (!showAddMultiLegButton || !props.showGroups || selectedGroup !== 'Multi') {
        return props.suffix ?? null;
      }
      return (
        <HStack background="backgroundDropdownGroupHeader" py="spacing8" px="spacing12">
          <Button
            variant={ButtonVariants.Primary}
            onClick={handleAdd}
            width="100%"
            size={FormControlSizes.Small}
            data-testid="add-multileg"
          >
            + Create New Multileg Symbol
          </Button>
        </HStack>
      );
    },
    [showAddMultiLegButton, props.showGroups, props.suffix, handleAdd]
  );

  return (
    <>
      <BaseSymbolSelector
        dropdownPlacement={dropdownPlacement}
        dropdownWidth={dropdownWidth}
        maxHeight={maxHeight}
        RenderGroupHeader={OrderFormGroupHeader}
        appearance={appearance}
        closeMenuRef={closeMenuRef}
        suffixCb={suffixCb}
        {...props}
        includeSedol={enableSEDOLDisplay}
        onSymbolChanged={onSymbolChanged}
        availableSecurities={availableSecurities}
        closeOnInputBlur={!isPopout && !props.showGroups}
      />
      {createMultilegDialog.isOpen && <CreateMultilegDialog {...createMultilegDialog} />}
    </>
  );
});

export default SymbolSelector;
